import { CallEvent, ConversationAnchorPoints, VisitStatusUpdateEvent } from '../../types';
import { isVwrFeatureEnabled } from '../../util';
import { initializeReporter, trackEventsConnection } from '../Tracker';
import { initializeLaunchDarkly } from '../FeatureStore';
import { Client as ClientSDK } from '../../js-sdk/src';
import { RootDispatch, Store } from '../../store';
import {
  Conversations,
  Organizations,
  Patients,
  Calls,
  TypingStatus,
  VirtualWaitingRoom,
  Messages,
} from '../../types/API';
import { getApiEnv } from './ApiEnv';

window.__SDK_VERSION__ = process.env.VERSION;

interface Client {
  models: any;
  calls: Calls;
  conversations: Conversations;
  messages: Messages;
  organizations: Organizations;
  patients: Patients;
  typingStatus: TypingStatus;
  virtualWaitingRoom: VirtualWaitingRoom;
}
export interface ClientConnector {
  client: Client;
  store: { dispatch: RootDispatch };
}

export class ClientConnector {
  constructor(TCClient: typeof ClientSDK, store: Store) {
    const apiEnv = getApiEnv();

    this.store = store;
    this.client = new TCClient({
      apiEnv,
      condensedReplays: false,
      events: {
        keepAlive: true,
        vwr: isVwrFeatureEnabled(),
      },
      partnerName: 'reactMobile',
      product: 'pfma',
      wantCondensedReplays: false,
      version: window.__SDK_VERSION__,
    });
    window.TCClient = this.client;

    try {
      initializeLaunchDarkly(apiEnv);
    } catch (err) {
      console.error(err);
    }

    initializeReporter(this.client);

    this.client.on('signedOut', () => {
      this.store.dispatch.currentPage.showInvalid();
      this.stopListeners();
    });
  }

  _processEvent = (payload: any) => {
    const { data, method, model } = payload;
    this.store.dispatch[model][method](data);
  };

  handleConnect = () => {
    trackEventsConnection();
  };

  handleConversationChange = (_resource: any, conversation: any) => {
    this._processEvent({
      data: conversation,
      method: 'updateConversation',
      model: 'conversation',
    });
  };

  handleConversationDelete = (_resource: any, conversation: any) => {
    this._processEvent({
      data: conversation,
      method: 'deleteConversation',
      model: 'conversation',
    });
  };

  handleMessageCreate = (_resource: any, message: any) => {
    this._processEvent({
      data: message,
      method: 'createMessage',
      model: 'messages',
    });
  };

  handleCallChange = (_resource: any, call: CallEvent) => {
    this._processEvent({
      data: Object.assign({}, call),
      method: 'handleCallEvent',
      model: 'call',
    });
  };

  handleCallDelete = (_resource: any, call: CallEvent) => {
    this._processEvent({
      data: Object.assign({}, call),
      method: 'handleCallDelete',
      model: 'call',
    });
  };

  handleMessageChange = (_resource: any, message: any) => {
    this._processEvent({
      data: message,
      method: 'updateMessage',
      model: 'messages',
    });
  };

  handleMessageDelete = (_resource: any, message: any) => {
    this._processEvent({
      data: message,
      method: 'removeMessage',
      model: 'messages',
    });
  };

  handleGroupMembersChange = (_resource: any, group: any) => {
    this._processEvent({
      data: group.members,
      method: 'addMember',
      model: 'conversation',
    });
  };

  handleReplay = ({ type }: { type: string }) => {
    this._processEvent({
      data: type,
      method: 'replayChange',
      model: 'messageLoad',
    });
  };

  handleVWRChange = (_resource: any, vwrEvent: CallEvent) => {
    this._processEvent({
      data: Object.assign({}, vwrEvent),
      method: 'handleVWREvent',
      model: 'virtualWaitingRoom',
    });
  };

  handleVwrVisitChange = (vwrVisitChangeEvent: VisitStatusUpdateEvent) => {
    this._processEvent({
      data: Object.assign({}, vwrVisitChangeEvent),
      method: 'handleVwrVisitChange',
      model: 'virtualWaitingRoom',
    });
  };

  selectConversation = async (
    conversation: any,
    options: { isVwrFeatureEnabled: boolean } = { isVwrFeatureEnabled: false }
  ) => {
    this.startListeners();
    await this.client.users.ensureUsers(
      conversation.counterParty.memberIds,
      conversation.organizationId
    );
    this.store.dispatch.conversation.setConversation(conversation);
    return this.client.conversations.selectConversation(conversation.id, {
      anchorPoint: ConversationAnchorPoints.FIRST_UNREAD_ITEM,
    });
  };

  handleNetworkStatusChange = async ({ networkStatus }: { networkStatus: string }) => {
    this._processEvent({
      data: networkStatus,
      method: 'handleNetworkStatusChange',
      model: 'networkStatus',
    });
  };

  startListeners = () => {
    this.client.events.on('connected', this.handleConnect);
    this.client.models.Call.on('afterEject', this.handleCallDelete);
    this.client.models.Call.on('afterInject', this.handleCallChange);
    this.client.models.Conversation.on('afterEject', this.handleConversationDelete);
    this.client.models.Conversation.on('afterInject', this.handleConversationChange);
    this.client.models.GroupMembersChange.on('afterInject', this.handleGroupMembersChange);
    this.client.models.Message.on('afterCreate', this.handleMessageCreate);
    this.client.models.Message.on('afterEject', this.handleMessageDelete);
    this.client.models.Message.on('afterInject', this.handleMessageChange);
    this.client.models.VirtualWaitingRoom.on('afterInject', this.handleVWRChange);
    this.client.on('advisory', this.handleReplay);
    this.client.virtualWaitingRoom.on('vwr:status:update', this.handleVwrVisitChange);

    this.client.on('networkStatus:change', this.handleNetworkStatusChange);

    this.client.events.connect();
  };

  stopListeners = () => {
    this.client.events.off('connected', this.handleConnect);
    this.client.models.Call.removeListener('afterEject', this.handleCallDelete);
    this.client.models.Call.removeListener('afterInject', this.handleCallChange);
    this.client.models.Conversation.removeListener('afterEject', this.handleConversationDelete);
    this.client.models.Conversation.removeListener('afterInject', this.handleConversationChange);
    this.client.models.GroupMembersChange.removeListener(
      'afterInject',
      this.handleGroupMembersChange
    );
    this.client.models.Message.removeListener('afterCreate', this.handleMessageCreate);
    this.client.models.Message.removeListener('afterEject', this.handleMessageDelete);
    this.client.models.Message.removeListener('afterInject', this.handleMessageChange);
    this.client.models.VirtualWaitingRoom.removeListener('afterInject', this.handleVWRChange);
    this.client.removeListener('advisory', this.handleReplay);
    this.client.virtualWaitingRoom.off('vwr:status:update', this.handleVwrVisitChange);

    this.client.off('networkStatus:change', this.handleNetworkStatusChange);

    this.client.events.disconnect();
  };
}
